/**
 * shadcn/ui-style wrapper around Radix ``@radix-ui/react-dropdown-menu``
 * (REQ-UI-028 — shadcn/ui component layer).
 *
 * The click-triggered sibling of the right-click :file:`./context-menu.tsx`
 * wrapper. Mirrors its conventions exactly — the ``cn`` merge helper,
 * ``forwardRef`` + ``displayName``, Meridian design-system tokens from
 * ``shared/design-system.md`` (``bg-bg-overlay`` surface, ``border-border``,
 * ``text-text-primary`` body, ``bg-bg-elevated`` focus/hover, ``text-text-muted``
 * for de-emphasised hints). The first consumer is the per-panel **Sort by**
 * gear menu (:file:`@/multi-pane/list-sort-menu.tsx`; REQ-UI-110), composed in
 * the dockview group-header right-actions slot per ``chrome-spec.md`` §Panel
 * header.
 *
 * # Why Radix DropdownMenu (not the context-menu, not a Popover)
 *
 * The Sort-by gear is a **button-triggered** menu of mutually-exclusive
 * choices (the sort field + the direction). Radix's ``DropdownMenu`` gives,
 * for free, the things this affordance needs and a hand-rolled menu would have
 * to re-implement:
 *
 *   1. **Click / Enter / Space** on the trigger opens the menu; ``Escape``
 *      closes it and restores focus to the trigger.
 *   2. **Keyboard parity** — arrow-key roving focus across items, type-ahead,
 *      and ``RadioItem`` selection by ``Enter`` / ``Space`` (REQ-UI-059 —
 *      operators work the chrome by keyboard).
 *   3. **Accessibility** — the trigger advertises ``aria-haspopup`` +
 *      ``aria-expanded``; the content is a focus-trapped ``role="menu"``; the
 *      ``RadioGroup`` / ``RadioItem`` carry ``role="menuitemradio"`` +
 *      ``aria-checked`` so a screen reader announces the current sort
 *      selection (REQ-UI-094).
 *
 * The (Popover-based) preferences gear (:file:`@/preferences/preferences-menu.tsx`)
 * hand-rolls its own menu semantics because it hosts editable forms + nested
 * sections; the Sort-by gear is a flat radio menu, so the ``DropdownMenu``
 * primitive is the smaller, more-accessible tool.
 *
 * This wrapper exposes only the parts the gear uses (``Root`` / ``Trigger`` /
 * ``Content`` / ``Label`` / ``Separator`` / ``RadioGroup`` / ``RadioItem``);
 * the unused Radix parts (checkbox / sub menus / plain items / groups) are
 * intentionally not re-exported to keep the surface small until a second
 * consumer needs them (mirroring the context-menu wrapper's restraint).
 *
 * Cross-references:
 *   - REQ-UI-028 (shadcn/ui component layer).
 *   - REQ-UI-059 (keyboard navigation) / REQ-UI-094 (labelled affordances).
 *   - REQ-UI-110 (operator list sort — the first consumer).
 *   - shared/design-system.md (tokens); shared/chrome-spec.md §Panel header.
 */
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * The dropdown-menu root. Wraps the trigger + portalled content; manages open
 * state in response to click / keyboard on the trigger.
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;

/**
 * The trigger button region. Render the gear button as
 * ``<DropdownMenuTrigger asChild>`` so the button element itself is the
 * trigger (no wrapper) — preserving its own ``aria-label`` / classes. Radix
 * wires the click + keyboard handlers + ``aria-haspopup`` / ``aria-expanded``
 * onto the child.
 */
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

/**
 * The portalled menu surface. Styled as a Meridian overlay popover
 * (``bg-bg-overlay`` + ``border-border`` + ``shadow-lg``), portalled to
 * ``<body>`` so it escapes the dockview header's overflow clip + z-stack
 * (the header strip is short; the menu must float over the panel body).
 * ``sideOffset`` defaults to 4 so the menu clears the gear button.
 */
export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-[12rem] overflow-hidden rounded-md border border-border",
          "bg-bg-overlay p-1 text-text-primary shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

/**
 * A non-interactive section label (e.g. "Sort by" / "Direction"). A small
 * de-emphasised heading sized to the menu rows; ``text-text-muted`` so it
 * reads as a divider caption rather than a selectable item.
 */
export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        "px-2 py-1.5 text-meta font-medium uppercase tracking-wide text-text-muted",
        className,
      )}
      {...props}
    />
  );
});

/** A thin horizontal rule between menu sections. */
export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn("my-1 h-px bg-border", className)}
      {...props}
    />
  );
});

/**
 * A radio group — the parent of the mutually-exclusive
 * :func:`DropdownMenuRadioItem`s. Forwards ``value`` / ``onValueChange`` to
 * Radix; Radix manages the ``aria-checked`` state on each child item.
 */
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

/**
 * A single radio choice (``role="menuitemradio"``). The selected item shows a
 * leading check glyph (a non-colour signal — colour alone never carries the
 * selection, REQ-UI-046/094); ``focus:bg-bg-elevated`` gives the roving-focus /
 * hover highlight; ``data-[disabled]`` dims it. Radix sets ``aria-checked`` so a
 * screen reader announces which sort option is active.
 */
export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-7 pr-2",
        "text-body-sm text-text-primary outline-none",
        "focus:bg-bg-elevated focus:text-text-primary",
        "data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:text-text-disabled",
        className,
      )}
      {...props}
    >
      <span className="absolute left-1.5 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          {/* Non-colour selection signal (REQ-UI-046/094): the check glyph,
           * not just a highlight, marks the active sort field/direction. */}
          <Check size={14} weight="bold" aria-hidden />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
